import React, { useState } from 'react';
import { CalendarEvent } from '../../../types/calendar';
import { Order } from '../../../types/order';
import { TodoTask } from '../../../types/todo';
import { Recipe } from '../../../types/recipe';
import {
  getCalendarDates,
  formatDateToISO,
  isToday,
  isCurrentMonth,
  getEventsForDate,
  sortEventsByTime
} from '../../../utils/calendarUtils';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { RootState } from '../../../store/store';
import { addTask } from '../../../store/slices/todoSlice';
import { deleteFilling } from '../../../store/slices/recipesSlice';
import { updateOrderStatus } from '../../../store/slices/ordersSlice';
import { v4 as uuidv4 } from 'uuid';
import EventCard from './EventCard';
import DayDetailsModal from './DayDetailsModal';
import OrderConfirmationModal from './OrderConfirmationModal';

interface OrderDetail {
  customerName: string;
  batches: number;
  orderDate: string;
  orderId: string;
}

interface PendingDrop {
  fillingName: string;
  batches: number;
  dateStr: string;
  unitType: 'batches' | 'units';
}

interface PendingMultiDrop {
  items: Array<{ fillingName: string; batches?: number; units?: number }>;
  dateStr: string;
  unitType: 'batches' | 'units';
  source: string;
}

interface MonthViewProps {
  currentDate: string;
  events: CalendarEvent[];
  orders: Order[];
  tasks: TodoTask[];
  onEventClick: (event: CalendarEvent) => void;
}

const MonthView: React.FC<MonthViewProps> = ({ currentDate, events, orders, tasks, onEventClick }) => {
  const dispatch = useAppDispatch();
  const calendarDates = getCalendarDates(currentDate);
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Redux selectors for orders and fillings
  const allOrders = useAppSelector((state: RootState) => state.orders.orders);
  const fillings = useAppSelector((state: RootState) => state.recipes.fillings);

  const [isDayModalOpen, setIsDayModalOpen] = useState(false);
  const [selectedDayDate, setSelectedDayDate] = useState<string | null>(null);
  const [dragOverDate, setDragOverDate] = useState<string | null>(null);
  const [pendingDrop, setPendingDrop] = useState<PendingDrop | null>(null);

  // Multi-drop state for handling multiple selected items
  const [pendingMultiDrop, setPendingMultiDrop] = useState<PendingMultiDrop | null>(null);
  const [currentMultiDropIndex, setCurrentMultiDropIndex] = useState(0);
  const [multiDropSelections, setMultiDropSelections] = useState<Map<string, string[]>>(new Map());

  const getOrdersForDate = (dateStr: string): Order[] => {
    return orders.filter(order => order.orderDate === dateStr);
  };

  const getTasksForDate = (dateStr: string): TodoTask[] => {
    return tasks.filter(task => task.dueDate === dateStr);
  };

  const getOrderDetailsForFilling = (fillingName: string, unitType: 'batches' | 'units' = 'batches'): OrderDetail[] => {
    // Filter active finalized orders
    const relevantOrders = allOrders.filter((order: Order) => order.isFinalized && order.status !== 'Completed');

    const orderDetails: OrderDetail[] = [];

    relevantOrders.forEach((order: Order) => {
      // Find items matching this filling
      const matchingItems = order.items.filter(item => item.recipeName === fillingName);

      matchingItems.forEach(item => {
        if (unitType === 'batches') {
          // Find recipe to calculate batches
          const recipe = fillings.find((f: Recipe) => f.id === item.recipeId);

          if (recipe && recipe.yield > 0) {
            const batches = Math.ceil(item.quantity / recipe.yield);

            orderDetails.push({
              customerName: order.customerName,
              batches,
              orderDate: order.orderDate,
              orderId: order.id
            });
          }
        } else {
          // For units, use raw quantity
          orderDetails.push({
            customerName: order.customerName,
            batches: item.quantity, // Using 'batches' field to store units
            orderDate: order.orderDate,
            orderId: order.id
          });
        }
      });
    });

    // Sort by order date (oldest first)
    return orderDetails.sort((a, b) => a.orderDate.localeCompare(b.orderDate));
  };

  const handleDayClick = (date: Date) => {
    const dateStr = formatDateToISO(date);
    setSelectedDayDate(dateStr);
    setIsDayModalOpen(true);
  };

  const handleCloseDayModal = () => {
    setIsDayModalOpen(false);
    setSelectedDayDate(null);
  };

  const handleConfirmDrop = (selectedOrderIds: string[]) => {
    if (!pendingDrop) return;

    const { fillingName, dateStr, unitType } = pendingDrop;
    const orderDetails = getOrderDetailsForFilling(fillingName, unitType);

    // Calculate total quantity from selected orders using IDs
    const selectedQuantity = orderDetails
      .filter(order => selectedOrderIds.includes(order.orderId))
      .reduce((sum, order) => sum + order.batches, 0);

    // Deduplicate order IDs in case same order appears multiple times
    const uniqueOrderIds = [...new Set(selectedOrderIds)];

    // Update order status to "In Progress" for selected Pending orders
    uniqueOrderIds.forEach(orderId => {
      const order = allOrders.find((o: Order) => o.id === orderId);
      if (order && order.status === 'Pending') {
        dispatch(updateOrderStatus({ id: orderId, status: 'In Progress' }));
      }
    });

    const now = new Date().toISOString();

    // Create task title based on unit type
    const unitLabel =
      unitType === 'batches'
        ? `batch${selectedQuantity === 1 ? '' : 'es'}`
        : `unit${selectedQuantity === 1 ? '' : 's'}`;

    const newTask: TodoTask = {
      id: uuidv4(),
      title: `prep ${selectedQuantity} ${unitLabel} of ${fillingName}`,
      description: '',
      status: 'pending',
      priority: 'medium',
      dueDate: dateStr,
      fillingName: fillingName,
      batchesNeeded: selectedQuantity,
      scheduledOrderIds: uniqueOrderIds,
      taskType: unitType === 'batches' ? 'batch' : 'fulfillment',
      createdAt: now,
      updatedAt: now
    };

    dispatch(addTask(newTask));
    setPendingDrop(null); // Close modal
  };

  const handleCancelDrop = () => {
    setPendingDrop(null); // Close modal without creating task
  };

  // Multi-drop handlers for sequential modal flow
  const handleConfirmCurrentMultiDrop = (selectedOrderIds: string[]) => {
    if (!pendingMultiDrop) return;

    const currentItem = pendingMultiDrop.items[currentMultiDropIndex];

    // Build updated selections Map synchronously (includes current item)
    const updatedSelections = new Map(multiDropSelections);
    updatedSelections.set(currentItem.fillingName, selectedOrderIds);

    // Check if this is the last item
    if (currentMultiDropIndex < pendingMultiDrop.items.length - 1) {
      // Not last item - update state for next modal
      setMultiDropSelections(updatedSelections);
      setCurrentMultiDropIndex(prev => prev + 1);
    } else {
      // Last item - pass all selections directly to avoid race condition
      createAllTasksFromMultiDrop(updatedSelections);
    }
  };

  const createAllTasksFromMultiDrop = (allSelections: Map<string, string[]>) => {
    if (!pendingMultiDrop) return;

    const { items, dateStr, unitType } = pendingMultiDrop;
    const now = new Date().toISOString();

    items.forEach(item => {
      const fillingName = item.fillingName;
      const orderDetails = getOrderDetailsForFilling(fillingName, unitType);
      const selectedOrderIds = allSelections.get(fillingName) || [];

      // Calculate total quantity from selected orders
      const selectedQuantity = orderDetails
        .filter(order => selectedOrderIds.includes(order.orderId))
        .reduce((sum, order) => sum + order.batches, 0);

      // Deduplicate order IDs
      const uniqueOrderIds = [...new Set(selectedOrderIds)];

      // Update order statuses
      uniqueOrderIds.forEach(orderId => {
        const order = allOrders.find((o: Order) => o.id === orderId);
        if (order && order.status === 'Pending') {
          dispatch(updateOrderStatus({ id: orderId, status: 'In Progress' }));
        }
      });

      // Create task
      const unitLabel =
        unitType === 'batches'
          ? `batch${selectedQuantity === 1 ? '' : 'es'}`
          : `unit${selectedQuantity === 1 ? '' : 's'}`;

      const newTask: TodoTask = {
        id: uuidv4(),
        title: `prep ${selectedQuantity} ${unitLabel} of ${fillingName}`,
        description: '',
        status: 'pending',
        priority: 'medium',
        dueDate: dateStr,
        fillingName,
        batchesNeeded: selectedQuantity,
        scheduledOrderIds: uniqueOrderIds,
        taskType: unitType === 'batches' ? 'batch' : 'fulfillment',
        createdAt: now,
        updatedAt: now
      };

      dispatch(addTask(newTask));
    });

    // Reset multi-drop state
    setPendingMultiDrop(null);
    setCurrentMultiDropIndex(0);
    setMultiDropSelections(new Map());
  };

  const handleCancelMultiDrop = () => {
    // Reset multi-drop state without creating tasks
    setPendingMultiDrop(null);
    setCurrentMultiDropIndex(0);
    setMultiDropSelections(new Map());
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>, dateStr: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverDate(dateStr);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOverDate(null);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>, dateStr: string) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverDate(null);

    try {
      const data = e.dataTransfer.getData('application/json');
      if (!data) return;

      const dragData = JSON.parse(data);
      const now = new Date().toISOString();

      // Handle drop from DayPlanner (Filling Prep section)
      if (dragData.source === 'dayPlanner') {
        if (dragData.isMultiSelect && dragData.items) {
          // Multi-item drop - show sequential modals
          setPendingMultiDrop({
            items: dragData.items,
            dateStr,
            unitType: 'batches',
            source: 'dayPlanner'
          });
        } else {
          // Single item drop (backward compatibility)
          const { fillingName, batches } = dragData.items?.[0] || dragData;
          setPendingDrop({
            fillingName,
            batches,
            dateStr,
            unitType: 'batches'
          });
        }
      }
      // Handle drop from DayPlanner (Order Fulfillment section)
      else if (dragData.source === 'dayPlannerOrderFulfillment') {
        if (dragData.isMultiSelect && dragData.items) {
          // Multi-item drop - show sequential modals
          setPendingMultiDrop({
            items: dragData.items,
            dateStr,
            unitType: 'units',
            source: 'dayPlannerOrderFulfillment'
          });
        } else {
          // Single item drop (backward compatibility)
          const { fillingName, units } = dragData.items?.[0] || dragData;
          setPendingDrop({
            fillingName,
            batches: units, // Store units in batches field (generic quantity)
            dateStr,
            unitType: 'units'
          });
        }
      }
      // Handle drop from RecipeCard (Fillings page)
      else if (dragData.recipeId) {
        const { recipeId, recipeName, recipeYield } = dragData;

        const newTask: TodoTask = {
          id: uuidv4(),
          title: `prep ${recipeYield} batches of ${recipeName}`,
          description: '',
          status: 'pending',
          priority: 'medium',
          dueDate: dateStr,
          fillingName: recipeName,
          batchesNeeded: recipeYield,
          recipeId: recipeId,
          taskType: 'general',
          createdAt: now,
          updatedAt: now
        };

        // Add task to the calendar date
        dispatch(addTask(newTask));

        // Remove the filling from the fillings list
        dispatch(deleteFilling(recipeId));
      }
    } catch (error) {
      console.error('Error handling drop:', error);
    }
  };

  const renderDateCell = (date: Date) => {
    const dateStr = formatDateToISO(date);
    const isCurrentMonthDate = isCurrentMonth(date, currentDate);
    const isTodayDate = isToday(date);
    const allDayEvents = sortEventsByTime(getEventsForDate(events, dateStr));

    // Sort events: incomplete first, completed last (for deliveries)
    const dayEvents = allDayEvents.sort((a, b) => {
      const aCompleted = a.completed ? 1 : 0;
      const bCompleted = b.completed ? 1 : 0;
      return aCompleted - bCompleted;
    });

    const dayOrders = getOrdersForDate(dateStr);
    const allDayTasks = getTasksForDate(dateStr);

    // Sort tasks: incomplete first, completed last
    const dayTasks = allDayTasks.sort((a, b) => {
      const aCompleted = a.status === 'completed' ? 1 : 0;
      const bCompleted = b.status === 'completed' ? 1 : 0;
      return aCompleted - bCompleted;
    });

    const isDraggingOver = dragOverDate === dateStr;

    const cellStyle = {
      ...styles.dateCell,
      ...(isCurrentMonthDate ? {} : styles.otherMonthDate),
      ...(isTodayDate ? styles.todayDate : {}),
      ...(isDraggingOver ? styles.dragOverDate : {}),
      cursor: 'pointer'
    };

    const dateNumberStyle = {
      ...styles.dateNumber,
      ...(isTodayDate ? styles.todayNumber : {})
    };

    return (
      <div
        key={dateStr}
        style={cellStyle}
        onClick={() => handleDayClick(date)}
        onDragOver={e => handleDragOver(e, dateStr)}
        onDragLeave={handleDragLeave}
        onDrop={e => handleDrop(e, dateStr)}
      >
        <div style={styles.dateCellHeader}>
          <span style={dateNumberStyle}>{date.getDate()}</span>
          {dayOrders.length > 0 && (
            <span style={styles.orderBadge}>
              {dayOrders.length} order{dayOrders.length > 1 ? 's' : ''}
            </span>
          )}
        </div>

        <div style={styles.eventsContainer}>
          {dayEvents.slice(0, 3).map(event => (
            <EventCard key={event.id} event={event} onClick={onEventClick} compact />
          ))}
          {dayEvents.length > 3 && <div style={styles.moreEvents}>+{dayEvents.length - 3} more</div>}
        </div>

        {dayTasks.length > 0 && (
          <div style={styles.tasksContainer}>
            {dayTasks.slice(0, 2).map(task => {
              const isCompleted = task.status === 'completed';
              const taskBadgeStyle = isCompleted
                ? { ...styles.taskBadge, ...styles.taskBadgeCompleted }
                : styles.taskBadge;

              return (
                <div key={task.id} style={taskBadgeStyle}>
                  <span style={styles.taskCheckbox}>{isCompleted ? '☑' : '☐'}</span>
                  <span style={styles.taskText}>{task.title}</span>
                </div>
              );
            })}
            {dayTasks.length > 2 && (
              <div style={styles.moreTasksBadge}>
                +{dayTasks.length - 2} more task{dayTasks.length - 2 > 1 ? 's' : ''}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={styles.container}>
      <div style={styles.weekdayHeader}>
        {weekDays.map(day => (
          <div key={day} style={styles.weekdayCell}>
            {day}
          </div>
        ))}
      </div>

      <div style={styles.grid}>{calendarDates.map(date => renderDateCell(date))}</div>

      {isDayModalOpen && selectedDayDate && (
        <DayDetailsModal
          isOpen={isDayModalOpen}
          selectedDate={selectedDayDate}
          events={getEventsForDate(events, selectedDayDate)}
          orders={allOrders}
          tasks={getTasksForDate(selectedDayDate)}
          onClose={handleCloseDayModal}
        />
      )}

      {pendingDrop && (
        <OrderConfirmationModal
          isOpen={true}
          fillingName={pendingDrop.fillingName}
          totalBatches={pendingDrop.batches}
          orderDetails={getOrderDetailsForFilling(pendingDrop.fillingName, pendingDrop.unitType)}
          onConfirm={handleConfirmDrop}
          onCancel={handleCancelDrop}
          unitType={pendingDrop.unitType}
        />
      )}

      {pendingMultiDrop && pendingMultiDrop.items[currentMultiDropIndex] && (
        <OrderConfirmationModal
          isOpen={true}
          fillingName={pendingMultiDrop.items[currentMultiDropIndex].fillingName}
          totalBatches={
            pendingMultiDrop.unitType === 'batches'
              ? pendingMultiDrop.items[currentMultiDropIndex].batches || 0
              : pendingMultiDrop.items[currentMultiDropIndex].units || 0
          }
          orderDetails={getOrderDetailsForFilling(
            pendingMultiDrop.items[currentMultiDropIndex].fillingName,
            pendingMultiDrop.unitType
          )}
          onConfirm={handleConfirmCurrentMultiDrop}
          onCancel={handleCancelMultiDrop}
          unitType={pendingMultiDrop.unitType}
        />
      )}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%'
  },
  weekdayHeader: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    borderBottom: '1px solid #e0e0e0'
  },
  weekdayCell: {
    padding: '12px',
    textAlign: 'center',
    fontWeight: '600',
    fontSize: '14px',
    color: '#666',
    borderRight: '1px solid #e0e0e0'
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gridAutoRows: '1fr',
    flex: 1,
    overflow: 'auto'
  },
  dateCell: {
    height: '120px',
    padding: '8px',
    borderRight: '1px solid #e0e0e0',
    borderBottom: '1px solid #e0e0e0',
    backgroundColor: 'white',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column'
  },
  otherMonthDate: {
    backgroundColor: '#fafafa',
    color: '#999'
  },
  todayDate: {
    backgroundColor: '#e3f2fd'
  },
  dragOverDate: {
    backgroundColor: '#c8e6c9',
    border: '2px dashed #4CAF50',
    boxShadow: '0 0 8px rgba(76, 175, 80, 0.3)'
  },
  dateCellHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px',
    flexShrink: 0
  },
  dateNumber: {
    fontWeight: '500',
    fontSize: '14px',
    color: '#333'
  },
  todayNumber: {
    backgroundColor: '#2196F3',
    color: 'white',
    borderRadius: '50%',
    width: '28px',
    height: '28px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  orderBadge: {
    fontSize: '10px',
    color: '#FF9800',
    backgroundColor: '#FFF3E0',
    padding: '2px 6px',
    borderRadius: '10px',
    fontWeight: '500'
  },
  eventsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    overflow: 'hidden',
    flex: '1 1 auto',
    minHeight: 0
  },
  moreEvents: {
    fontSize: '11px',
    color: '#666',
    marginTop: '4px',
    paddingLeft: '4px'
  },
  tasksContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    marginTop: '8px',
    overflow: 'hidden',
    flex: '0 1 auto'
  },
  taskBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 6px',
    backgroundColor: '#FFF9C4',
    borderRadius: '4px',
    border: '1px solid #FBC02D',
    fontSize: '10px',
    lineHeight: '1.2'
  },
  taskCheckbox: {
    fontSize: '12px',
    color: '#F57F17',
    flexShrink: 0
  },
  taskText: {
    color: '#333',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontWeight: '500'
  },
  moreTasksBadge: {
    fontSize: '10px',
    color: '#F57F17',
    backgroundColor: '#FFF9C4',
    padding: '2px 6px',
    borderRadius: '4px',
    marginTop: '2px',
    fontWeight: '500'
  },
  taskBadgeCompleted: {
    backgroundColor: '#F5F5F5',
    borderColor: '#BDBDBD',
    opacity: 0.6,
    cursor: 'default',
    color: '#757575'
  }
};

export default MonthView;
